import React, { useEffect } from 'react';
import { ActionIcon, Paper, ScrollArea, Table, Button, Group, Badge, Stack, Text } from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { IconDownload, IconTruckDelivery, IconPlus, IconFlask } from '@tabler/icons-react';
import { useParams } from 'react-router';
import { useDisclosure } from '@mantine/hooks';
import { useGetLabTests } from '../../../global/entities/LabTest/queries/useGetLabTests.tsx';
import { useGetPendingLabTests } from '../../../global/entities/LabTest/queries/useGetPendingLabTests.tsx';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import Utils from '../../../global/helpers/utils.ts';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { ManualLabResultModal } from './ManualLabResultModal.tsx';
import { useSessionProvider } from '../../../global/provider/sessionContext.tsx';
import { ILabTest } from '../../../global/entities/LabTest/LabTest.ts';
interface ITherapeuticDrugMonitoringProps {
    patientStore?: PatientStore;
}

const TherapeuticDrugMonitoring: React.FC<ITherapeuticDrugMonitoringProps> = inject('patientStore')(
    observer(({ patientStore }) => {
        const { t } = useTranslation();
        const params = useParams<{ id: string; tabId: string }>();
        const { data: labTests } = useGetLabTests(patientStore?.patientId);
        //const { data: pendingLabTests } = useGetPendingLabTests(patientStore?.patientId);
        const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
        const { currentUser } = useSessionProvider();

        // Check if current user is an HCP (has healthcareProfessionalID)
        const isHCP = !!currentUser?.healthcareProfessionalID;
        let pendingLabTests: ILabTest[] | undefined;
        if (isHCP) {
            const { data: pendingLabTestsData } = useGetPendingLabTests(patientStore?.patientId);
            pendingLabTests = pendingLabTestsData;
        }

        const { setSorting, sortBy, reverseSortDirection, setSortedData, sortedData } = useTable();

        useEffect(() => {
            setSortedData([]);

            if (labTests?.length) 
                setSortedData(labTests);
            
        }, [labTests, setSortedData]);

        return (
            <Stack gap="md" w="100%">
                {/* Pending Lab Results Section - Only show for HCPs */}
                {isHCP && (
                    <Paper p="md" withBorder w="100%">
                        <Group justify="space-between" align="center">
                            <Group gap="sm" align="center">
                                <IconFlask size={20} />
                                <Text size="lg" fw={600}>{t('biomarkers.pendingLabTests')}</Text>
                                <Badge color="orange" size="sm">
                                    {pendingLabTests?.length || 0}
                                </Badge>
                            </Group>
                            
                            <Button
                                leftSection={<IconPlus size={16} />}
                                onClick={openModal}
                                size="sm"
                                disabled={pendingLabTests?.length === 0}
                            >
                                {t('biomarkers.enterManualResult')}
                            </Button>
                        </Group>
                    </Paper>
                )}

                {/* Completed Lab Tests Table */}
                <Paper w="100%">
                    <ScrollArea w="100%">
                        <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Th
                                    sorted={sortBy === 'dateOrdered'}
                                    reversed={reverseSortDirection}
                                    onSort={() => setSorting('dateOrdered', sortedData)}
                                >
                                    {t('global.dateOrdered')}
                                </Th>

                                <Th
                                    sorted={sortBy === 'dateMeasured'}
                                    reversed={reverseSortDirection}
                                    onSort={() => setSorting('dateMeasured', sortedData)}
                                >
                                    {t('global.dateMeasured')}
                                </Th>
                                <Th
                                    sorted={sortBy === 'description'}
                                    reversed={reverseSortDirection}
                                    onSort={() => setSorting('description', sortedData)}
                                >
                                    {t('global.description')}
                                </Th>
                                <Th
                                    sorted={sortBy === 'specimen'}
                                    reversed={reverseSortDirection}
                                    onSort={() => setSorting('specimen', sortedData)}
                                >
                                    {t('global.specimen')}
                                </Th>
                                <Th
                                    sorted={sortBy === 'value'}
                                    reversed={reverseSortDirection}
                                    onSort={() => setSorting('value', sortedData)}
                                >
                                    {t('global.value')}
                                </Th>
                                <Th
                                    sorted={sortBy === 'unit'}
                                    reversed={reverseSortDirection}
                                    onSort={() => setSorting('unit', sortedData)}
                                >
                                    {t('global.unit')}
                                </Th>
                                {params.tabId === 'hcpDosageSurveillance' && (
                                    <Table.Th w={100}>{t('table.therapeuticDrugMonitoring.labDetails')}</Table.Th>
                                )}
                                {params.tabId === 'hcpDosageSurveillance' && (
                                    <Table.Th w={60}>{t('table.therapeuticDrugMonitoring.tracking')}</Table.Th>
                                )}
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {sortedData?.map((row, index) => (
                                <Table.Tr
                                    key={index}
                                    style={{
                                        background:
                                            index % 2 === 1 ? 'var(--Background-Gray-light, #E9ECEF)' : undefined,
                                    }}
                                >
                                    <Table.Td>{Utils.formatDateToYYYYMMDD(row.dateOrdered)}</Table.Td>
                                    <Table.Td>{`${Utils.formatDateToYYYYMMDD(row.dateMeasured)} ${Utils.formatDateToTime(row.dateMeasured)}`}</Table.Td>
                                    <Table.Td>{row.description}</Table.Td>
                                    <Table.Td>{row.specimen}</Table.Td>
                                    <Table.Td>{row.value}</Table.Td>
                                    <Table.Td>{row.unit}</Table.Td>
                                    {params.tabId === 'hcpDosageSurveillance' && (
                                        <Table.Td align={'center'}>
                                            <ActionIcon variant={'light'} color={'gray'}>
                                                <IconDownload />
                                            </ActionIcon>
                                        </Table.Td>
                                    )}
                                    {params.tabId === 'hcpDosageSurveillance' && (
                                        <Table.Td align={'center'}>
                                            <ActionIcon variant={'light'} color={'gray'}>
                                                <IconTruckDelivery />
                                            </ActionIcon>
                                        </Table.Td>
                                    )}
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </ScrollArea>
            </Paper>

            {/* Manual Lab Result Modal - Only render for HCPs */}
            {isHCP && (
                <ManualLabResultModal
                    opened={modalOpened}
                    onClose={closeModal}
                    pendingLabTests={pendingLabTests || []}
                />
            )}
        </Stack>
        );
    }),
);

const WrappeTherapeuticDrugMonitoringTable: React.FC = () => {
    return (
        <TableContextProvider>
            <TherapeuticDrugMonitoring />
        </TableContextProvider>
    );
};

export default WrappeTherapeuticDrugMonitoringTable;
